import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import posthogLib from 'posthog-js';

import httpInstances from '@/api/axiosInstance';
import { METADATA_TERMS_FIELD } from '@/constants/constants';
import { clearStoragesFromTokensAndUserInfo, setTripTokenToStorage } from '@/services/storage';
import { UserAccess, UserMetadata, UserSubscription } from '@/types/auth';
import { detectEnvironment } from '@/utils/detectEnvironment';
import { retrieveFromUserMetadata } from '@/utils/retrieveFromUserMetadata';

export type AuthApiState = {
  status: 'idle' | 'loading' | 'failed' | 'fulfilled' | 'notFound' | 'onSuppressionList';
  error: string | null;
  userEmail: string | null;
  userAccess: UserAccess | null;
  metadata: UserMetadata[] | null;
  terms: string | null;
  hsObjectId: string | null;
  subscription: UserSubscription | null;
};

type LoginApiErrorResponse = {
  errors: string[];
};

type ErrorResponse = {
  message: string;
};

const initialState: AuthApiState = {
  status: 'idle',
  error: null,
  userEmail: null,
  userAccess: null,
  hsObjectId: null,
  terms: null,
  metadata: null,
  subscription: null
};

export const loginGX = createAsyncThunk(
  'loginGX',
  async (request: { email: string; captchaToken: string }, { rejectWithValue }) => {
    try {
      await httpInstances.axiosInternalInstance.post(
        `${detectEnvironment() === 'staging' ? '/staging/' : '/'}login`,
        { email: request.email },
        { headers: { captchaToken: request.captchaToken } }
      );
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue({ data: error.response.data, status: error.response.status });
      }
      throw error;
    }
  }
);

export const loginTrip = createAsyncThunk(
  'loginTrip',

  async (gxToken: string, { rejectWithValue }) => {
    try {
      const { data } = await httpInstances.axiosInternalInstance.get(
        `${detectEnvironment() === 'staging' ? '/staging/' : '/'}token`,
        {
          params: { token: gxToken }
        }
      );
      setTripTokenToStorage(data.token);
      return data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue({ data: error.response.data, status: error.response.status });
      }
      throw error;
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetStatus(state) {
      state.status = 'idle';
    },
    acceptTerms(state, action: PayloadAction<string>) {
      const terms = action.payload;
      state.terms = terms;
      saveTerms(terms, state.userEmail ?? '');
    },
    logout() {
      clearStoragesFromTokensAndUserInfo();
      posthogLib.reset();
      return initialState;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loginGX.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginGX.fulfilled, state => {
        state.status = 'fulfilled';
      })
      .addCase(loginGX.rejected, (state, action) => {
        const response = action.payload as { data: unknown; status: number };
        console.error(action.payload);

        if (response?.status === 404) {
          state.status = 'notFound';
        } else if (
          response?.status === 403 &&
          (response?.data as LoginApiErrorResponse)?.errors?.some(error =>
            error.includes('is on suppression list')
          )
        ) {
          state.status = 'onSuppressionList';
        } else {
          console.error(action.payload);
          posthogLib.capture('user_login_error', {
            payload: action.payload
          });
          state.status = 'failed';
          if (action.payload && (action.payload as ErrorResponse).message) {
            state.error = (action.payload as ErrorResponse).message;
          } else {
            state.error = action.error.message ?? 'Login failed';
          }
        }
      })
      .addCase(loginTrip.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginTrip.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.userEmail = action.payload?.userEmail;
        state.userAccess = UserAccess[action.payload?.access as UserAccess];
        state.hsObjectId = action.payload?.hsObjectId;
        state.metadata = action.payload?.metadata;
        state.terms = retrieveFromUserMetadata(action.payload?.metadata, METADATA_TERMS_FIELD);
        state.subscription = action.payload?.subscription;
      })
      .addCase(loginTrip.rejected, (state, action) => {
        state.status = 'failed';
        if (action.payload) {
          const response = action.payload as { data: string; status: number };
          state.error = response.data ?? 'Login failed';
        } else {
          state.error = action.error.message ?? 'Login failed';
        }
      });
  }
});

const saveTerms = async (terms: string, email: string) => {
  return httpInstances.axiosGoInstance.patch(`/users/${email}`, { terms });
};

export const { resetStatus, logout, acceptTerms } = authSlice.actions;

export default authSlice.reducer;
